import { useRef, useState, useCallback } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, ProgressBar, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFileAlt,
  faCheckCircle,
  faExclamationTriangle,
  faEdit,
  faArrowLeft
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useHistory } from "react-router-dom";
import config from "../../config/config.json";
import { apiCall } from "../../_services/apiCall";
import "./DocumentUploadPage.css";
import Sidebar from "../sidebar/sidebar";
import DocumentForm from "./DocumentForm";
import { useFrameworks, useSources, useFinancialYears } from "../../hooks/useApiData";

const DocumentUploadPage = (props) => {
  const fileInputRef = useRef();
  const history = useHistory();

  // API data hooks
  const frameworks = useFrameworks();
  const sources = useSources();
  const financialYears = useFinancialYears();

  // Memoized options
  const sourceOptions = sources.map(s => ({
    value: s.id,
    label: s.unitCode || s.location.area
  }));

  const financialYearOptions = financialYears.map(fy => ({
    value: fy.id,
    label: fy.financial_year_value
  }));

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [parsedDocument, setParsedDocument] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Form state
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Event handlers
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
      setUploadSuccess(false);
      setParsedDocument(null);
      setSaveSuccess(false);
      setSaveError(null);
    }
  }, []);

  const handleFileUpload = useCallback(async () => {
    if (!selectedFile) {
      setUploadError("Please select a file to upload.");
      return;
    }

    try {
      setUploadLoading(true);
      setUploadError(null);
      setUploadProgress(10);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);

      setUploadProgress(30);

      // Make API call to parse document
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}documents/upload`,
        {},
        formData,
        "POST"
      );

      setUploadProgress(80);

      if (isSuccess && data) {
        setUploadProgress(100);
        setParsedDocument(data);
        setUploadSuccess(true);
      } else {
        setUploadError("Failed to parse the document. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      setUploadError("An error occurred while uploading the document. Please try again.");
    } finally {
      setUploadLoading(false);
      setUploadProgress(0);
    }
  }, [selectedFile]);

  const handleSave = useCallback((savedData, updatedData) => {
    // Update the parsed document with new data
    setParsedDocument(prevDocument => ({
      ...prevDocument,
      ...updatedData
    }));

    setSaveSuccess(true);
    setSaveError(null);

    // Clear success message after 3 seconds
    setTimeout(() => {
      setSaveSuccess(false);
    }, 3000);
  }, []);

  const resetAll = useCallback(() => {
    setSelectedFile(null);
    setUploadError(null);
    setUploadSuccess(false);
    setParsedDocument(null);
    setUploadProgress(0);
    setSaveSuccess(false);
    setSaveError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const navigateBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/documents');
    }
  }, [history]);

  const renderFileMetadata = useCallback((fileMetadata) => {
    if (!fileMetadata) return null;

    return (
      <div className="file-metadata-section mb-4">
        <h6>File Metadata</h6>
        <div className="file-metadata-grid">
          <Row>
            <Col md={6}>
              <div className="metadata-item">
                <strong>File Name:</strong>
                <span className="metadata-value">{fileMetadata.fileName || 'N/A'}</span>
              </div>
              <div className="metadata-item">
                <strong>File Size:</strong>
                <span className="metadata-value">
                  {fileMetadata.fileSize
                    ? `${(fileMetadata.fileSize / 1024 / 1024).toFixed(2)} MB`
                    : 'N/A'}
                </span>
              </div>
              <div className="metadata-item">
                <strong>MIME Type:</strong>
                <span className="metadata-value">{fileMetadata.mimeType || 'N/A'}</span>
              </div>
              <div className="metadata-item">
                <strong>Provider:</strong>
                <span className="metadata-value">{fileMetadata.provider || 'N/A'}</span>
              </div>
              <div className="metadata-item">
                <strong>Temporary:</strong>
                <span className="metadata-value">
                  {fileMetadata.isTemporary ? 'Yes' : 'No'}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="metadata-item">
                <strong>File UUID:</strong>
                <span className="metadata-value uuid-text">{fileMetadata.uuid || 'N/A'}</span>
              </div>
              <div className="metadata-item">
                <strong>File Hash:</strong>
                <span className="metadata-value hash-text">{fileMetadata.hash || 'N/A'}</span>
              </div>
              <div className="metadata-item">
                <strong>Uploaded By:</strong>
                <span className="metadata-value">{fileMetadata.uploadedById || 'N/A'}</span>
              </div>
              <div className="metadata-item">
                <strong>Created At:</strong>
                <span className="metadata-value">
                  {fileMetadata.createdAt
                    ? moment(fileMetadata.createdAt).format("DD MMM YYYY, HH:mm")
                    : 'N/A'}
                </span>
              </div>
              {fileMetadata.url && (
                <div className="metadata-item">
                  <strong>File URL:</strong>
                  <span className="metadata-value">
                    <a
                      href={fileMetadata.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="file-url-link"
                    >
                      View File
                    </a>
                  </span>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </div>
    );
  }, []);

  const renderUploadSection = useCallback(() => (
    <div className="upload-section">
      <div className="file-upload-container mb-3">
        <div className="file-upload-area p-4 text-center">
          <FontAwesomeIcon icon={faFileAlt} size="3x" className="text-muted mb-3" />
          <h5 className="mb-3">Select a document to upload and parse</h5>
          <p className="text-muted mb-3">
            Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG
          </p>
          <div className="file-input-wrapper">
            <Form.Control
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="file-input"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="file-input-label">
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Choose File
            </label>
          </div>
          {selectedFile && (
            <Alert variant="info" className="text-start mt-3">
              <strong>Selected File:</strong> {selectedFile.name}<br />
              <strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </Alert>
          )}
        </div>
      </div>

      {uploadProgress > 0 && uploadProgress < 100 && (
        <div className="mb-3">
          <label className="form-label">Upload Progress</label>
          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
        </div>
      )}

      {uploadError && (
        <Alert variant="danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {uploadError}
        </Alert>
      )}

      <div className="text-center">
        <Button
          variant="primary"
          size="lg"
          onClick={handleFileUpload}
          disabled={!selectedFile || uploadLoading}
        >
          {uploadLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Upload & Parse Document
            </>
          )}
        </Button>
      </div>
    </div>
  ), [selectedFile, uploadProgress, uploadError, uploadLoading, handleFileSelect, handleFileUpload]);

  const renderSuccessSection = useCallback(() => (
    <div className="upload-success">
      <Alert variant="success">
        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
        Document uploaded and parsed successfully!
      </Alert>

      {parsedDocument.isExisting && (
        <Alert variant="info">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          <strong>File Found:</strong> Great news! We found this file in your system. You're viewing the existing document - feel free to review or update any information as needed.
        </Alert>
      )}

      <Alert variant="info">
        <FontAwesomeIcon icon={faEdit} className="me-2" />
        <strong>Review & Edit:</strong> Please review the parsed information below and make any necessary corrections before saving.
      </Alert>

      <div className="parsed-document-details">
        <h5>Document Information</h5>

        {parsedDocument.fileMetadata && renderFileMetadata(parsedDocument.fileMetadata)}

        <DocumentForm
          editDocument={parsedDocument}
          frameworks={frameworks}
          sourceOptions={sourceOptions}
          financialYearOptions={financialYearOptions}
          onSave={handleSave}
          loading={saveLoading}
          error={saveError}
          success={saveSuccess}
        />
      </div>

      <div className="action-buttons mt-4 d-flex justify-content-center gap-3">
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={resetAll}
        >
          Upload Another
        </Button>
      </div>
    </div>
  ), [
    parsedDocument, 
    frameworks, 
    sourceOptions, 
    financialYearOptions, 
    handleSave, 
    saveLoading, 
    saveError, 
    saveSuccess, 
    resetAll,
    renderFileMetadata
  ]);

  return (
    <div className="document-management-page">
      <Row className="flex-nowrap m-0 p-0">
        {/* Sidebar */}
        <Col xs={2} className="sidebar-container p-0">
          <Sidebar activePage="documents" />
        </Col>
        <Col xs={10} className="content-area p-0">
          <Container fluid className="document-upload-page px-3">
            <div className="page-header d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <Button
                  variant="outline-secondary"
                  onClick={navigateBack}
                  className="me-3"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back
                </Button>
                <h2 className="page-title mb-0">Upload New Document</h2>
              </div>

              <Button
                variant="outline-primary"
                onClick={resetAll}
                disabled={uploadLoading || saveLoading}
              >
                Reset Form
              </Button>
            </div>

            <Row>
              <Col xs={12}>
                <Card className="upload-card">
                  <Card.Body className="p-4">
                    {!uploadSuccess && !parsedDocument ? 
                      renderUploadSection() : 
                      renderSuccessSection()
                    }
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
    </div>
  );
};

export default DocumentUploadPage;